import React from 'react';
import { useFormikContext } from 'formik';
import { useToasts } from 'react-toast-notifications';
import { Button, Grid, Typography } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import moment from 'moment';
import api from '../api';
import { useAppData } from '../contexts/appContext';
import AlertDialog from './AlertDialog';
import useFortifySigning from './useFortifySigning';

const ETA_FIELDS = [
	'etaReturnId',
	'etaSubmissionId',
	'returnStatus',
	'etaReturnStatus',
	'filingStartedAt',
	'etaUnrecordedSubmissions'
];

const RECONCILE_TOAST_APPEARANCE = {
	recorded: 'success',
	not_found: 'warning',
	multiple_candidates: 'warning'
};

const filingClaimIsReleasable = (filingStartedAt, releaseAfterMinutes) =>
	!filingStartedAt ||
	Date.now() - new Date(filingStartedAt).getTime() >=
		releaseAfterMinutes * 60 * 1000;

const reconcileMessage = ({ message, candidates = [] }) =>
	candidates.length > 1
		? `${message}: ${candidates.map(({ uuid }) => uuid).join(', ')}`
		: message;

const unrecordedSubmissionsOf = values =>
	Array.isArray(values.etaUnrecordedSubmissions)
		? values.etaUnrecordedSubmissions
		: [];

const UNRECORDED_ACTIONS = {
	adopt: {
		title: 'Adopt this ETA credit note?',
		confirmationText: 'Adopt',
		path: 'adoptinvoicereturnsubmission',
		success: 'Credit Note Adopted',
		description: uuid =>
			`ETA credit note ${uuid} will be recorded as this return's filed credit note. ETA is checked first: the credit note must carry this return's number and must not be cancelled, rejected or invalid. Adopt it only if it is the credit note this return should have on ETA.`
	},
	dismiss: {
		title: 'Dismiss this ETA credit note?',
		confirmationText: 'Dismiss',
		path: 'dismissinvoicereturnsubmission',
		success: 'Unrecorded Credit Note Dismissed',
		description: uuid =>
			`Cancel credit note ${uuid} on the ETA portal before dismissing it. It is only dismissed when ETA reports it as cancelled, rejected or invalid.`
	}
};

function UnrecordedSubmissionsBanner({
	submissions,
	canAdopt,
	busy,
	onAction
}) {
	const [pending, setPending] = React.useState(null);
	if (!submissions.length) return null;
	const action = pending && UNRECORDED_ACTIONS[pending.action];
	return (
		<Alert severity="warning">
			<Typography variant="body2" gutterBottom>
				ETA accepted credit notes for this return that were never recorded. It
				can not be filed again until each one is adopted as the return&apos;s
				credit note or cancelled on the ETA portal and dismissed.
			</Typography>
			{submissions.map(({ uuid }) => (
				<Grid container spacing={1} alignItems="center" key={uuid}>
					<Grid item>
						<Typography variant="body2">{uuid}</Typography>
					</Grid>
					{canAdopt && (
						<Grid item>
							<Button
								size="small"
								variant="contained"
								color="primary"
								disabled={busy}
								onClick={() => setPending({ action: 'adopt', uuid })}
							>
								Adopt
							</Button>
						</Grid>
					)}
					<Grid item>
						<Button
							size="small"
							variant="contained"
							disabled={busy}
							onClick={() => setPending({ action: 'dismiss', uuid })}
						>
							Dismiss
						</Button>
					</Grid>
				</Grid>
			))}
			<AlertDialog
				title={action ? action.title : ''}
				open={Boolean(action)}
				setOpen={open => {
					if (!open) setPending(null);
				}}
				confirmationText={action ? action.confirmationText : 'OK'}
				cancellationText="Cancel"
				onConfirm={() => onAction(pending)}
			>
				{action ? action.description(pending.uuid) : null}
			</AlertDialog>
		</Alert>
	);
}

function FileInvoiceReturnButton() {
	const { config } = useAppData();
	const { addToast } = useToasts();
	const { values, setFieldValue } = useFormikContext();
	const [busy, setBusy] = React.useState(false);
	const [confirmRelease, setConfirmRelease] = React.useState(false);
	const [, setNow] = React.useState(Date.now());
	const signedDocumentHash = React.useRef(null);
	const { releaseAfterMinutes } = config.etaConfig.invoiceReturnConfig;

	React.useEffect(() => {
		if (values.returnStatus !== 'filing') return undefined;
		const timer = setInterval(() => setNow(Date.now()), 30 * 1000);
		return () => clearInterval(timer);
	}, [values.returnStatus]);

	const updateValues = data => {
		ETA_FIELDS.forEach(field => setFieldValue(field, data[field] ?? ''));
	};
	const reloadValues = () =>
		api
			.get(`/api/invoicereturn/${values.id}`)
			.then(res => updateValues(res.data))
			.catch(() => null);
	const run = (request, message) => {
		setBusy(true);
		return request()
			.then(res => {
				updateValues(res.data);
				addToast(message, { appearance: 'success', autoDismiss: true });
			})
			.catch(reloadValues)
			.finally(() => setBusy(false));
	};
	const fileReturn = signature =>
		run(
			() =>
				api.post(
					`/api/fileinvoicereturn/${values.id}`,
					signature
						? { signature, documentHash: signedDocumentHash.current }
						: undefined
				),
			'Invoice Return Filed'
		);
	const { requestSignature, signingDialog } = useFortifySigning({
		onSigned: fileReturn,
		failureMessage: 'Failed to sign invoice return'
	});
	const onFileClick = () => {
		if (config.etaConfig.serverSigning) return fileReturn();
		setBusy(true);
		return api
			.get(`/api/gov-invoice-return/${values.id}`)
			.then(res => {
				signedDocumentHash.current = res.data.documentHash;
				return requestSignature(res.data.serializedInvoice);
			})
			.catch(reloadValues)
			.finally(() => setBusy(false));
	};
	const onCancelClick = () =>
		run(
			() => api.post(`/api/cancelinvoicereturn/${values.id}`),
			'Invoice Return Cancelled on ETA'
		);
	const onRefreshClick = () =>
		run(
			() => api.post(`/api/refreshinvoicereturnstatus/${values.id}`),
			'ETA Status Refreshed'
		);
	const onReconcileClick = () => {
		setBusy(true);
		return api
			.post(`/api/reconcileinvoicereturnfiling/${values.id}`)
			.then(res => {
				updateValues(res.data.invoiceReturn);
				addToast(reconcileMessage(res.data), {
					appearance: RECONCILE_TOAST_APPEARANCE[res.data.outcome] || 'info',
					autoDismiss: res.data.outcome === 'recorded'
				});
			})
			.catch(reloadValues)
			.finally(() => setBusy(false));
	};
	const onReleaseConfirmed = () =>
		run(
			() => api.post(`/api/releaseinvoicereturnfiling/${values.id}`),
			'Filing Claim Released'
		);
	const onUnrecordedAction = ({ action, uuid }) =>
		run(
			() =>
				api.post(`/api/${UNRECORDED_ACTIONS[action].path}/${values.id}`, {
					uuid
				}),
			UNRECORDED_ACTIONS[action].success
		);
	const unrecordedSubmissions = unrecordedSubmissionsOf(values);
	const banner = (
		<UnrecordedSubmissionsBanner
			submissions={unrecordedSubmissions}
			canAdopt={values.returnStatus === 'not_filed' && !values.etaReturnId}
			busy={busy}
			onAction={onUnrecordedAction}
		/>
	);
	const withBanner = content => (
		<>
			{banner}
			{content}
		</>
	);

	if (values.returnStatus === 'filing') {
		const releasable = filingClaimIsReleasable(
			values.filingStartedAt,
			releaseAfterMinutes
		);
		const startedAt = values.filingStartedAt
			? moment(values.filingStartedAt).format('DD/MM/YYYY HH:mm:ss')
			: 'an unknown time';
		return withBanner(
			<Grid container spacing={1} alignItems="center">
				<Grid item>
					<Button variant="contained" disabled>
						Filing on ETA...
					</Button>
				</Grid>
				<Grid item>
					<Typography variant="body2">Filing started at {startedAt}</Typography>
				</Grid>
				<Grid item>
					<Button
						onClick={onReconcileClick}
						variant="contained"
						color="primary"
						disabled={busy}
					>
						Reconcile
					</Button>
				</Grid>
				{releasable && (
					<Grid item>
						<Button
							onClick={() => setConfirmRelease(true)}
							variant="contained"
							disabled={busy}
						>
							Release Claim
						</Button>
					</Grid>
				)}
				<AlertDialog
					title="Release the filing claim?"
					open={confirmRelease}
					setOpen={setConfirmRelease}
					confirmationText="Release Claim"
					cancellationText="Cancel"
					onConfirm={onReleaseConfirmed}
				>
					Check the ETA portal for a credit note with return number{' '}
					{values.returnNumber} first. Release the claim only if ETA does not
					hold it: after the release the return can be filed again, and filing a
					return that ETA already holds creates a duplicate credit note.
				</AlertDialog>
			</Grid>
		);
	}
	if (values.etaReturnId) {
		return withBanner(
			<Grid container spacing={1}>
				<Grid item>
					<Button onClick={onCancelClick} variant="contained" disabled={busy}>
						Cancel Return
					</Button>
				</Grid>
				<Grid item>
					<Button onClick={onRefreshClick} variant="contained" disabled={busy}>
						Refresh ETA Status
					</Button>
				</Grid>
			</Grid>
		);
	}
	if (unrecordedSubmissions.length) return banner;
	return (
		<>
			<Button
				onClick={onFileClick}
				variant="contained"
				color="primary"
				disabled={busy}
			>
				File Return
			</Button>
			{signingDialog}
		</>
	);
}

export default FileInvoiceReturnButton;
